/**
 * EnrichmentEligibilitySummary — renders the eligibility bucketing,
 * remaining-allowance messaging, and credit-cost note for
 * BulkEnrichAutoModal. Pure presentational: all counts, allowance content,
 * and credit content are computed by the caller and passed in as props.
 */

import { CheckCircle, XCircle } from '@untitledui/icons';

import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { EntityBadgeByIds } from '@/lib/crm/components/badges';

/**
 * Shared explanatory note rendered in every non-loading / non-error state.
 * Clarifies two things so the user has full context before running a manual
 * bulk enrich:
 *   1. Auto-enrichment already runs on its own, so this manual action is
 *      optional — users don't necessarily need it.
 *   2. Already-enriched (within the past year) or in-progress contacts are
 *      skipped and never re-enriched.
 * Hoisted to module scope per the stable-references / no-inline-components
 * rules.
 */
function EnrichmentInfoNote() {
  return (
    <p className='text-xs text-fg-tertiary'>
      Contacts are enriched automatically over time, so you usually do not need
      to run this manually. Contacts that were already enriched in the past
      year, or that have enrichment in progress, are skipped and will not be
      enriched again.
    </p>
  );
}

export interface EnrichmentEligibilitySummaryProps {
  orgId: number;
  isLoading: boolean;
  isError: boolean;
  isSelectAllMode?: boolean;
  /** Total number of contacts loaded on this page slice (explicit-selection mode) */
  totalContactsCount: number;
  /** Total matching records in select-all mode (totalCount ?? contacts.length) */
  selectAllCount: number;
  willEnrichEntityIds: string[];
  inProgressEntityIds: string[];
  recentlyEnrichedEntityIds: string[];
  allowanceContent: React.ReactNode;
  creditContent: React.ReactNode;
}

export function EnrichmentEligibilitySummary({
  orgId,
  isLoading,
  isError,
  isSelectAllMode,
  totalContactsCount,
  selectAllCount,
  willEnrichEntityIds,
  inProgressEntityIds,
  recentlyEnrichedEntityIds,
  allowanceContent,
  creditContent,
}: EnrichmentEligibilitySummaryProps) {
  if (isLoading) {
    return (
      <div className='flex items-center gap-2 text-sm text-fg-secondary'>
        <LoadingIndicator type='line-simple' size='sm' />
        Checking eligibility...
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex items-center gap-2 text-sm text-error-500'>
        <XCircle className='w-4 h-4' />
        We couldn't check eligibility. Try again.
      </div>
    );
  }

  if (isSelectAllMode) {
    // Select-all: the caller's `contacts` is only the loaded page slice
    // (often empty), so a per-contact eligibility breakdown would always
    // read "0 will be enriched" and contradict the CTA. The backend
    // resolves eligibility server-side and skips already-enriched /
    // in-progress contacts, so surface the total matched count and explain
    // the skip behaviour instead.
    return (
      <>
        <div className='flex items-center gap-2 text-sm'>
          <CheckCircle className='w-4 h-4 text-success-500' />
          <span className='text-fg-primary font-medium'>{selectAllCount}</span>
          <span className='text-fg-secondary'>
            contact{selectAllCount === 1 ? '' : 's'} selected
          </span>
        </div>
        <EnrichmentInfoNote />
        {allowanceContent}
        {creditContent}
      </>
    );
  }

  const willEnrichCount = willEnrichEntityIds.length;
  const inProgressCount = inProgressEntityIds.length;
  const recentlyEnrichedCount = recentlyEnrichedEntityIds.length;

  return (
    <>
      {/* Will enrich */}
      <div className='flex items-center gap-2 text-sm'>
        <CheckCircle className='w-4 h-4 text-success-500' />
        <span className='text-fg-primary font-medium'>{willEnrichCount}</span>
        <span className='text-fg-secondary'>
          contact{willEnrichCount === 1 ? '' : 's'} will be enriched
        </span>
      </div>
      {willEnrichCount > 0 && (
        <div className='ml-6'>
          <EntityBadgeByIds
            entityIds={willEnrichEntityIds}
            orgId={orgId}
            maxVisible={5}
            variant='xs'
          />
        </div>
      )}

      {/* Skipped — in progress */}
      {inProgressCount > 0 && (
        <>
          <div className='flex items-center gap-2 text-sm'>
            <XCircle className='w-4 h-4 text-fg-quaternary' />
            <span className='text-fg-tertiary'>{inProgressCount}</span>
            <span className='text-fg-quaternary'>
              skipped — enrichment in progress
            </span>
          </div>
          <div className='ml-6'>
            <EntityBadgeByIds
              entityIds={inProgressEntityIds}
              orgId={orgId}
              maxVisible={5}
              variant='xs'
            />
          </div>
        </>
      )}

      {/* Skipped — recently enriched */}
      {recentlyEnrichedCount > 0 && (
        <>
          <div className='flex items-center gap-2 text-sm'>
            <XCircle className='w-4 h-4 text-fg-quaternary' />
            <span className='text-fg-tertiary'>{recentlyEnrichedCount}</span>
            <span className='text-fg-quaternary'>
              skipped — recently enriched (&lt;1 year)
            </span>
          </div>
          <div className='ml-6'>
            <EntityBadgeByIds
              entityIds={recentlyEnrichedEntityIds}
              orgId={orgId}
              maxVisible={5}
              variant='xs'
            />
          </div>
        </>
      )}

      {/* Tail block: summary + a persistent note that already-enriched
          contacts are never re-enriched. Grouped under a single divider. */}
      <div className='space-y-1 pt-2 border-t border-border-secondary'>
        {(inProgressCount > 0 || recentlyEnrichedCount > 0) &&
          willEnrichCount > 0 && (
            <p className='text-sm text-fg-tertiary'>
              {willEnrichCount} of {totalContactsCount} selected contacts will
              be enriched.
            </p>
          )}
        {willEnrichCount === 0 && (
          <p className='text-sm text-fg-secondary'>
            None of the selected contacts are eligible for enrichment.
          </p>
        )}
        <EnrichmentInfoNote />
        {allowanceContent}
        {creditContent}
      </div>
    </>
  );
}
